import { hasValue } from '@repo/utils';
import { action, computed, flow, makeObservable, observable, reaction } from 'mobx';

import { EventEmitter } from '../../shared/EventEmitter';
import type { TrajectoryContext, TrajectorySelection, TrajectoryWellboreRef } from '../types/data';
import type { TGraphTrajectoryView } from '../types/graph-trajectory';
import type { ReadonlyDeep } from '../types/readonly-deep';
import {
  copyTrajectorySelectedReferenceTree,
  EMPTY_TRAJECTORY_SELECTED_REFERENCE_TREE,
  type TTrajectorySelectedReferenceTree,
} from '../types/selected-reference-data';
import type { TTrajectory } from '../types/trajectory';
import { OperationAbortedError } from '../utils/errors';
import { pickActiveOrFirst } from '../utils/pick-active-or-first';
import { getTrajectoryLineColor } from '../utils/trajectory-line-colors';

import type { TrajectoriesApi } from './api/trajectories-api';
import { collectFactTrajectoryRequests, type TWellboreTrajectoryRequest } from './collect-fact-trajectory-requests';
import { FactTrajectoryEntity, FactTrajectoryPointEntity, isLocalNewTrajectoryId } from './entities';
import {
  applyFactWriteDtoToEntity,
  mapEntityToFactWriteDto,
  mapEntityToTrajectoryDto,
  mapFactTrajectory,
  resolveActiveFlag,
} from './mappers';
import type { TFactTrajectoryPoint, TrajectoryPlanOrFact } from './model';
import { resolveCorrespondingTrajectoryId } from './resolve-corresponding-trajectory-id';

type TInternalWellboreRequest = {
  wellboreId: number;
  trajectoryIds: Set<number>;
};

type TTrajectoryTableEventMap = {
  newPoints: FactTrajectoryPointEntity[];
  updatePoints: FactTrajectoryPointEntity[];
  deletePoints: string[];
};

export type TrajectoryDraftIdProvider = () => number | null;
export type TrajectoryUpsertedListener = (saved: TTrajectory, previousId?: number) => void;
export type TrajectoryDeletedListener = (trajectoryId: number) => void;

let nextTempTrajectoryId = -1;

export class TrajectoryService {
  @observable.ref private _wellId: number | null = null;
  @observable.ref private _planOrFact: TrajectoryPlanOrFact | null = null;
  @observable.ref private _wellbores: ReadonlyDeep<TrajectoryWellboreRef[]> | null = null;
  @observable.ref private _selectedWellboreId: number | null = null;
  @observable.ref private _selectedTrajectoryId: number | null = null;
  @observable.ref private _selectedReferenceData: TTrajectorySelectedReferenceTree =
    copyTrajectorySelectedReferenceTree(EMPTY_TRAJECTORY_SELECTED_REFERENCE_TREE);

  @observable.ref private _mainTrajectory: TGraphTrajectoryView | null = null;
  @observable.ref private _referenceTrajectories: TGraphTrajectoryView[] = [];
  /** Full trajectory catalog for the selected wellbore (sidebar / table / graph main). */
  @observable.ref private _wellboreTrajectories: FactTrajectoryEntity[] = [];
  @observable private _isWellboreTrajectoriesLoading = false;
  /** Info-only catalog of the parent wellbore (parent trajectory select). */
  @observable.ref private _parentWellboreTrajectories: TTrajectory[] = [];
  @observable private _isParentWellboreTrajectoriesLoading = false;
  @observable private _isSaving = false;

  private readonly api: TrajectoriesApi;
  private abortController: AbortController | null = null;
  private loadGeneration = 0;
  private parentAbortController: AbortController | null = null;
  private parentLoadGeneration = 0;
  @observable.ref private getDraftId: TrajectoryDraftIdProvider | null = null;
  @observable.ref private _draftId: number | null = null;
  private onTrajectoryUpserted: TrajectoryUpsertedListener | null = null;
  private onTrajectoryDeleted: TrajectoryDeletedListener | null = null;
  private lastRequests: TInternalWellboreRequest[] = [];
  private loadedEntitiesByWellbore = new Map<number, FactTrajectoryEntity[]>();
  readonly eventEmitter = new EventEmitter<TTrajectoryTableEventMap>();

  constructor(api: TrajectoriesApi) {
    this.api = api;
    makeObservable(this);
  }

  /** Host provides a live getter (must read observables, not a closed-over primitive). */
  @action.bound
  setDraftIdProvider(getDraftId: TrajectoryDraftIdProvider | null): void {
    this.getDraftId = getDraftId;
  }

  /** Optional host hook (e.g. header trajectory catalog sync). */
  @action.bound
  setTrajectoryUpsertedListener(listener: TrajectoryUpsertedListener | null): void {
    this.onTrajectoryUpserted = listener;
  }

  /** Optional host hook (e.g. header trajectory catalog sync after delete). */
  @action.bound
  setTrajectoryDeletedListener(listener: TrajectoryDeletedListener | null): void {
    this.onTrajectoryDeleted = listener;
  }

  @computed
  get wellId(): number | null {
    return this._wellId;
  }

  @computed
  get wellbores(): ReadonlyDeep<TrajectoryWellboreRef[]> | null {
    return this._wellbores;
  }

  @computed
  get selectedWellboreId(): number | null {
    return this._selectedWellboreId;
  }

  @computed
  get selectedWellbore(): TrajectoryWellboreRef | null {
    if (!hasValue(this._selectedWellboreId)) {
      return null;
    }

    return this._wellbores?.find((item) => item.id === this._selectedWellboreId) ?? null;
  }

  @computed
  get parentWellboreId(): number | null {
    const parentId = this.selectedWellbore?.parentWellboreId;

    return hasValue(parentId) ? parentId : null;
  }

  @computed
  get hasParentWellbore(): boolean {
    return hasValue(this.parentWellboreId);
  }

  @computed
  get parentWellboreTrajectories(): ReadonlyArray<{ id: number; title: string }> {
    return this._parentWellboreTrajectories.map((item) => ({
      id: item.id,
      title: (item.citation?.title ?? '').trim(),
    }));
  }

  @computed
  get isParentWellboreTrajectoriesLoading(): boolean {
    return this._isParentWellboreTrajectoriesLoading;
  }

  /** Active parent trajectory, otherwise the first in the parent catalog. */
  @computed
  get defaultParentTrajectoryId(): number | null {
    return pickActiveOrFirst(this._parentWellboreTrajectories)?.id ?? null;
  }

  @computed
  get selectedTrajectoryId(): number | null {
    return this._selectedTrajectoryId;
  }

  @computed
  get mainTrajectory(): TGraphTrajectoryView | null {
    return this._mainTrajectory;
  }

  @computed
  get mainFactTrajectory(): FactTrajectoryEntity | null {
    return this._mainTrajectory?.entity ?? null;
  }

  @computed
  get graphTrajectories(): TGraphTrajectoryView[] {
    if (!this._mainTrajectory) {
      return this._referenceTrajectories;
    }

    return [this._mainTrajectory, ...this._referenceTrajectories];
  }

  @computed
  get selectedReferenceData(): TTrajectorySelectedReferenceTree {
    return this._selectedReferenceData;
  }

  @computed
  get hasWell(): boolean {
    return hasValue(this._wellId);
  }

  @computed
  get hasWellboreSelection(): boolean {
    return hasValue(this._selectedWellboreId);
  }

  @computed
  get hasTrajectorySelection(): boolean {
    return hasValue(this._selectedTrajectoryId);
  }

  @computed
  get wellboreTrajectories(): FactTrajectoryEntity[] {
    return this._wellboreTrajectories;
  }

  @computed
  get isWellboreTrajectoriesLoading(): boolean {
    return this._isWellboreTrajectoriesLoading;
  }

  @computed
  get isSaving(): boolean {
    return this._isSaving;
  }

  @action.bound
  setContext(context: TrajectoryContext): void {
    this._wellId = context.wellId;

    if ('planOrFact' in context) {
      this._planOrFact = context.planOrFact ?? null;
    }
  }

  @action.bound
  setWellbores(wellbores: ReadonlyDeep<TrajectoryWellboreRef[]> | null): void {
    this._wellbores = wellbores;
  }

  @action.bound
  setSelection(selection: TrajectorySelection): void {
    if ('wellboreId' in selection) {
      const nextWellboreId = selection.wellboreId ?? null;

      if (this._selectedWellboreId !== nextWellboreId) {
        this._selectedWellboreId = nextWellboreId;
        this._wellboreTrajectories = [];
        this._parentWellboreTrajectories = [];
        this.clearVisualTrajectories();
      }
    }

    if ('trajectoryId' in selection) {
      this._selectedTrajectoryId = selection.trajectoryId ?? null;
    }
  }

  @action.bound
  onSelectedReferenceDataChange(data: TTrajectorySelectedReferenceTree): void {
    this._selectedReferenceData = copyTrajectorySelectedReferenceTree(data);
  }

  getTrajectoryById(trajectoryId: number | null | undefined): FactTrajectoryEntity | null {
    if (!hasValue(trajectoryId)) {
      return null;
    }

    return this._wellboreTrajectories.find((item) => item.id === trajectoryId) ?? null;
  }

  getFieldValue<K extends keyof TFactTrajectoryPoint>(
    entityId: string | null | undefined,
    field: K,
  ): TFactTrajectoryPoint[K] | null {
    if (!entityId) {
      return null;
    }

    return this.mainFactTrajectory?.getPointField(entityId, field) ?? null;
  }

  @action.bound
  addMainPoint(): FactTrajectoryPointEntity | null {
    const trajectory = this.mainFactTrajectory;

    if (!trajectory || trajectory.isStationsSourceLocked) {
      return null;
    }

    const point = new FactTrajectoryPointEntity();
    trajectory.addPoint(point);
    this.eventEmitter.emit('newPoints', [point]);

    return point;
  }

  @action.bound
  updateMainPointField(entityId: string, patch: Partial<TFactTrajectoryPoint>): void {
    const trajectory = this.mainFactTrajectory;

    if (!trajectory) {
      return;
    }

    const point = trajectory.pointsMap.get(entityId);

    if (!point || trajectory.isPointLocked(point)) {
      return;
    }

    if (trajectory.isMdAtOrBelowTieOn(patch.md)) {
      return;
    }

    trajectory.updatePoint(entityId, patch);

    const updated = trajectory.pointsMap.get(entityId);

    if (!updated) {
      return;
    }

    this.eventEmitter.emit('updatePoints', [updated]);
  }

  @computed
  get areMainStationsReadOnly(): boolean {
    return this.mainFactTrajectory?.isStationsSourceLocked ?? false;
  }

  isMainPointLocked(point: FactTrajectoryPointEntity | null | undefined): boolean {
    return this.mainFactTrajectory?.isPointLocked(point) ?? false;
  }

  @action.bound
  deleteMainPoints(entityIds: string[]): string[] {
    if (entityIds.length === 0) {
      return [];
    }

    const removedIds = this.mainFactTrajectory?.removePointsFromUppermost(entityIds) ?? [];

    if (removedIds.length > 0) {
      this.eventEmitter.emit('deletePoints', removedIds);
    }

    return removedIds;
  }

  @action.bound
  createLocalTrajectory(): FactTrajectoryEntity | null {
    const wellboreId = this._selectedWellboreId;

    if (!hasValue(wellboreId)) {
      return null;
    }

    const entity = new FactTrajectoryEntity(
      {
        id: nextTempTrajectoryId--,
        wellboreId,
        citation: {
          title: '',
          description: '',
        },
      },
      [],
    );

    this._wellboreTrajectories = [...this._wellboreTrajectories, entity];
    this.loadedEntitiesByWellbore.set(wellboreId, this._wellboreTrajectories);

    return entity;
  }

  @action.bound
  removeWellboreTrajectory(trajectoryId: number): void {
    this._wellboreTrajectories = this._wellboreTrajectories.filter((item) => item.id !== trajectoryId);

    const wellboreId = this._selectedWellboreId;

    if (hasValue(wellboreId)) {
      this.loadedEntitiesByWellbore.set(wellboreId, this._wellboreTrajectories);
    }
  }

  @action.bound
  private reselectAfterTrajectoryRemoved(removedTrajectoryId: number): void {
    if (this._selectedTrajectoryId !== removedTrajectoryId) {
      return;
    }

    const next = this._wellboreTrajectories[0] ?? null;
    this.setSelection({ trajectoryId: next?.id ?? null });
  }

  /** DELETE fact trajectory draft. Local (negative id) items are removed without a request. */
  @flow.bound
  async *deleteFactTrajectory(trajectoryId: number) {
    if (isLocalNewTrajectoryId(trajectoryId)) {
      this.removeWellboreTrajectory(trajectoryId);
      this.reselectAfterTrajectoryRemoved(trajectoryId);
      this.applyGraphViews();
      this.onTrajectoryDeleted?.(trajectoryId);
      return;
    }

    const wellId = this._wellId;
    const draftId = this._draftId ?? this.getDraftId?.() ?? null;

    if (!hasValue(wellId) || !hasValue(draftId)) {
      throw new Error('wellId or draftId is not presented');
    }

    this._isSaving = true;

    try {
      yield this.api.deleteFactTrajectory(wellId, draftId, trajectoryId);
      this.removeWellboreTrajectory(trajectoryId);
      this.reselectAfterTrajectoryRemoved(trajectoryId);
      this.applyGraphViews();
      this.onTrajectoryDeleted?.(trajectoryId);
    } finally {
      this._isSaving = false;
    }
  }

  /** Maps entity → fact DTO, PUT via API, commits into the catalog entity. Uses host wellId + draftId. */
  @flow.bound
  async *upsertFactTrajectory(entity: FactTrajectoryEntity) {
    const wellId = this._wellId;
    const draftId = this._draftId ?? this.getDraftId?.() ?? null;
    const previousId = entity.id;

    if (!hasValue(wellId) || !hasValue(draftId)) {
      throw new Error('wellId or draftId is not presented');
    }

    this._isSaving = true;

    try {
      const body = mapEntityToFactWriteDto(entity);
      const response: Awaited<ReturnType<TrajectoriesApi['upsertFactTrajectory']>> =
        yield this.api.upsertFactTrajectory(wellId, draftId, body);

      const previousPointIds = entity.points.map((point) => point.entityId);

      applyFactWriteDtoToEntity(entity, response);

      const nextPointIds = new Set(entity.points.map((point) => point.entityId));
      const removedPointIds = previousPointIds.filter((id) => !nextPointIds.has(id));

      if (removedPointIds.length > 0) {
        this.eventEmitter.emit('deletePoints', removedPointIds);
      }

      if (entity.points.length > 0) {
        this.eventEmitter.emit('updatePoints', [...entity.points]);
      }

      const wasNew = previousId < 0;

      this.setSelection({ trajectoryId: entity.id });
      this.applyGraphViews();
      this.onTrajectoryUpserted?.(mapEntityToTrajectoryDto(entity), wasNew ? previousId : undefined);

      return entity;
    } finally {
      this._isSaving = false;
    }
  }

  /** Force re-fetch of loaded wellbore trajectories (e.g. after host synced trajectory-data). */
  @flow.bound
  async *reloadWellboreTrajectories() {
    yield this.syncLoadedTrajectories();
  }

  @flow.bound
  private async *syncParentWellboreTrajectories() {
    this.parentAbortController?.abort();
    this.parentAbortController = null;

    const generation = ++this.parentLoadGeneration;
    const parentWellboreId = this.parentWellboreId;

    if (!hasValue(parentWellboreId) || this._planOrFact === 'plan') {
      this._parentWellboreTrajectories = [];
      this._isParentWellboreTrajectoriesLoading = false;
      return;
    }

    this._isParentWellboreTrajectoriesLoading = true;

    const abortController = new AbortController();
    this.parentAbortController = abortController;
    const draftId = this._draftId ?? this.getDraftId?.() ?? null;

    try {
      const trajectories: TTrajectory[] = yield this.api.fetchByWellbore(
        parentWellboreId,
        abortController.signal,
        draftId,
        { infoOnly: true },
      );

      if (generation !== this.parentLoadGeneration) {
        return;
      }

      this._parentWellboreTrajectories = trajectories.map((item) => {
        const active = resolveActiveFlag(item);

        return {
          ...item,
          ...(active !== undefined ? { active } : {}),
        };
      });
    } catch (e) {
      if (generation !== this.parentLoadGeneration || e instanceof OperationAbortedError) {
        return;
      }

      this._parentWellboreTrajectories = [];
    } finally {
      if (generation === this.parentLoadGeneration && this.parentAbortController === abortController) {
        this.parentAbortController = null;
        this._isParentWellboreTrajectoriesLoading = false;
      }
    }
  }

  @flow.bound
  private async *syncLoadedTrajectories() {
    this.abortController?.abort();
    this.abortController = null;

    const generation = ++this.loadGeneration;

    if (this._planOrFact === 'plan') {
      this.lastRequests = [];
      this.loadedEntitiesByWellbore = new Map();
      this._wellboreTrajectories = [];
      this.clearVisualTrajectories();
      this._isWellboreTrajectoriesLoading = false;
      return;
    }

    const collected: TWellboreTrajectoryRequest[] = collectFactTrajectoryRequests({
      planOrFact: this._planOrFact,
      selectedWellboreId: this._selectedWellboreId,
      selectedReferenceData: this._selectedReferenceData,
    });

    const requests: TInternalWellboreRequest[] = collected.map((item) => ({
      wellboreId: item.wellboreId,
      trajectoryIds: new Set(item.trajectoryIds),
    }));

    this.lastRequests = requests;

    if (requests.length === 0) {
      this.loadedEntitiesByWellbore = new Map();
      this._wellboreTrajectories = [];
      this.clearVisualTrajectories();
      this._isWellboreTrajectoriesLoading = false;
      return;
    }

    this._isWellboreTrajectoriesLoading = true;
    const wellboreIds = [...new Set(requests.map((item) => item.wellboreId))];

    const abortController = new AbortController();
    this.abortController = abortController;

    try {
      const results: PromiseSettledResult<{ wellboreId: number; trajectories: TTrajectory[] }>[] =
        yield Promise.allSettled(
          wellboreIds.map(async (wellboreId) => {
            const trajectories = await this.api.fetchByWellbore(
              wellboreId,
              abortController.signal,
              this.resolveDraftId(wellboreId),
            );
            return { wellboreId, trajectories };
          }),
        );

      if (generation !== this.loadGeneration) {
        return;
      }

      const loadedByWellbore = new Map<number, TTrajectory[]>();

      for (const result of results) {
        if (result.status === 'fulfilled') {
          loadedByWellbore.set(result.value.wellboreId, result.value.trajectories);
        }
      }

      this.remapSelectedTrajectoryToDraft(requests, loadedByWellbore);
      this.replaceLoadedEntities(loadedByWellbore);
      this.applyGraphViews();
    } catch (e) {
      if (generation !== this.loadGeneration || e instanceof OperationAbortedError) {
        return;
      }

      this.loadedEntitiesByWellbore = new Map();
      this._wellboreTrajectories = [];
      this.applyGraphViews();
    } finally {
      if (generation === this.loadGeneration && this.abortController === abortController) {
        this.abortController = null;
        this._isWellboreTrajectoriesLoading = false;
      }
    }
  }

  @action.bound
  private replaceLoadedEntities(loadedByWellbore: Map<number, TTrajectory[]>): void {
    const next = new Map<number, FactTrajectoryEntity[]>();

    for (const [wellboreId, rawList] of loadedByWellbore) {
      const entities = rawList.map(mapFactTrajectory);
      next.set(wellboreId, entities);

      if (wellboreId === this._selectedWellboreId) {
        this._wellboreTrajectories = entities;
      }
    }

    if (!hasValue(this._selectedWellboreId) || !next.has(this._selectedWellboreId)) {
      this._wellboreTrajectories = [];
    }

    this.loadedEntitiesByWellbore = next;
  }

  @action.bound
  private applyGraphViews(): void {
    if (this._planOrFact === 'plan') {
      this.clearVisualTrajectories();
      return;
    }

    const mainId = this._selectedTrajectoryId;
    const mainWellboreId = this._selectedWellboreId;
    let main: TGraphTrajectoryView | null = null;
    const references: TGraphTrajectoryView[] = [];
    const seenIds = new Set<number>();

    const toView = (entity: FactTrajectoryEntity, role: TGraphTrajectoryView['role']): TGraphTrajectoryView => ({
      id: entity.id,
      title: entity.title,
      wellboreId: entity.wellboreId,
      role,
      color: getTrajectoryLineColor(entity.id),
      entity,
    });

    if (hasValue(mainId) && hasValue(mainWellboreId)) {
      const mainEntity =
        this._wellboreTrajectories.find((item) => item.id === mainId) ??
        this.loadedEntitiesByWellbore.get(mainWellboreId)?.find((item) => item.id === mainId);

      if (mainEntity) {
        main = toView(mainEntity, 'main');
        seenIds.add(mainEntity.id);
      }
    }

    for (const request of this.lastRequests) {
      const entities = this.loadedEntitiesByWellbore.get(request.wellboreId) ?? [];

      for (const entity of entities) {
        if (seenIds.has(entity.id) || !request.trajectoryIds.has(entity.id)) {
          continue;
        }

        seenIds.add(entity.id);
        references.push(toView(entity, 'reference'));
      }
    }

    this._mainTrajectory = main;
    this._referenceTrajectories = references;
  }

  /** When a section draft is active, selected id may still be the published trajectory. */
  @action.bound
  private remapSelectedTrajectoryToDraft(
    requests: TInternalWellboreRequest[],
    loadedByWellbore: Map<number, TTrajectory[]>,
  ): void {
    const draftId = this._draftId ?? this.getDraftId?.() ?? null;
    const wellboreId = this._selectedWellboreId;

    if (!hasValue(draftId) || !hasValue(wellboreId)) {
      return;
    }

    const loaded = loadedByWellbore.get(wellboreId) ?? [];
    const mappedId =
      resolveCorrespondingTrajectoryId(this._selectedTrajectoryId, loaded) ?? pickActiveOrFirst(loaded)?.id ?? null;

    if (!hasValue(mappedId) || mappedId === this._selectedTrajectoryId) {
      return;
    }

    const selectedRequest = requests.find((item) => item.wellboreId === wellboreId);
    selectedRequest?.trajectoryIds.add(mappedId);
    this._selectedTrajectoryId = mappedId;
  }

  @action.bound
  private clearVisualTrajectories(): void {
    this._mainTrajectory = null;
    this._referenceTrajectories = [];
  }

  private isCurrentWellWellbore(wellboreId: number): boolean {
    if (this._selectedWellboreId === wellboreId) {
      return true;
    }

    return this._wellbores?.some((item) => item.id === wellboreId) ?? false;
  }

  private resolveDraftId(wellboreId: number): number | null {
    if (!this.isCurrentWellWellbore(wellboreId)) {
      return null;
    }

    return this._draftId ?? this.getDraftId?.() ?? null;
  }

  @action.bound
  private dispose(): void {
    this.abortController?.abort();
    this.abortController = null;
    this.loadGeneration += 1;
    this.parentAbortController?.abort();
    this.parentAbortController = null;
    this.parentLoadGeneration += 1;
    this._wellId = null;
    this._planOrFact = null;
    this._wellbores = null;
    this._selectedWellboreId = null;
    this._selectedTrajectoryId = null;
    this._draftId = null;
    this.getDraftId = null;
    this._selectedReferenceData = copyTrajectorySelectedReferenceTree(EMPTY_TRAJECTORY_SELECTED_REFERENCE_TREE);
    this._wellboreTrajectories = [];
    this._isWellboreTrajectoriesLoading = false;
    this._parentWellboreTrajectories = [];
    this._isParentWellboreTrajectoriesLoading = false;
    this.lastRequests = [];
    this.loadedEntitiesByWellbore = new Map();
    this.clearVisualTrajectories();
  }

  init = (): VoidFunction => {
    const disposeFetchReaction = reaction(
      () => ({
        wellId: this._wellId,
        planOrFact: this._planOrFact,
        wellboreId: this._selectedWellboreId,
        draftId: this.getDraftId?.() ?? null,
        selectedReferenceData: this._selectedReferenceData,
      }),
      ({ draftId }) => {
        this._draftId = draftId;
        void this.syncLoadedTrajectories();
      },
      { fireImmediately: true },
    );

    const disposeParentCatalogReaction = reaction(
      () => ({
        planOrFact: this._planOrFact,
        parentWellboreId: this.parentWellboreId,
        draftId: this.getDraftId?.() ?? null,
      }),
      () => {
        void this.syncParentWellboreTrajectories();
      },
      { fireImmediately: true },
    );

    const disposeSelectionReaction = reaction(
      () => this._selectedTrajectoryId,
      () => {
        if (this._isWellboreTrajectoriesLoading) {
          return;
        }

        this.applyGraphViews();
      },
    );

    return () => {
      disposeFetchReaction();
      disposeParentCatalogReaction();
      disposeSelectionReaction();
      this.dispose();
    };
  };
}
